import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { ApiOk } from "@/app/types/api";
import {
  mapServiceOfferDetailResponse,
  mapServiceOfferListResponse,
  mapServiceSeriesBookingsResponse,
  mapServiceSeriesDetailResponse,
} from "@/app/_store/api/mappers/serviceMappers";

export const serviceSeriesApi = createApi({
  reducerPath: "serviceSeriesApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api" }),
  tagTypes: ["ServiceOffers", "ServiceSeries"],
  endpoints: (builder) => ({
    getAllServiceOffers: builder.query<any[], void>({
      query: () => ({ url: "/service-offers", method: "GET" }),
      transformResponse: (response: ApiOk<any[]>) =>
        mapServiceOfferListResponse(response?.data),
      providesTags: (result) => [
        { type: "ServiceOffers", id: "LIST" },
        ...(Array.isArray(result)
          ? result
              .map((offer) => offer?.id)
              .filter((id) => Number.isFinite(id))
              .map((id) => ({ type: "ServiceOffers" as const, id: String(id) }))
          : []),
      ],
    }),
    getServiceOfferById: builder.query<any | null, string | number>({
      query: (id) => ({ url: `/service-offers/${id}`, method: "GET" }),
      transformResponse: (response: ApiOk<any>) =>
        mapServiceOfferDetailResponse(response?.data),
      providesTags: (result, _error, id) => [
        { type: "ServiceOffers", id: String(id) },
        ...(Array.isArray(result?.series)
          ? result.series
              .map((series: any) => series?.id)
              .filter((seriesId: unknown) => Number.isFinite(seriesId as number))
              .map((seriesId: number) => ({
                type: "ServiceSeries" as const,
                id: String(seriesId),
              }))
          : []),
      ],
    }),
    createServiceOffer: builder.mutation<any, Record<string, unknown>>({
      query: (body) => ({ url: "/service-offers", method: "POST", body }),
      transformResponse: (response: ApiOk<any>) => response?.data,
      invalidatesTags: [{ type: "ServiceOffers", id: "LIST" }],
    }),
    updateServiceOffer: builder.mutation<
      any,
      { id: number | string; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/service-offers/${id}`,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (response: ApiOk<any>) => response?.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ServiceOffers", id: "LIST" },
        { type: "ServiceOffers", id: String(id) },
      ],
    }),
    getServiceSeriesById: builder.query<any | null, string | number>({
      query: (id) => ({ url: `/service-series/${id}`, method: "GET" }),
      transformResponse: (response: ApiOk<{ series?: unknown }>) =>
        mapServiceSeriesDetailResponse(response?.data?.series),
      providesTags: (_result, _error, id) => [
        { type: "ServiceSeries", id: String(id) },
      ],
    }),
    updateServiceSeries: builder.mutation<
      any,
      { id: number | string; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/service-series/${id}`,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (response: ApiOk<any>) => response?.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ServiceOffers", id: "LIST" },
        { type: "ServiceSeries", id: String(id) },
      ],
    }),
    createServiceSeries: builder.mutation<
      any,
      { courseId: number | string; data: Record<string, unknown> }
    >({
      query: ({ courseId, data }) => ({
        url: `/service-offers/${courseId}/series`,
        method: "POST",
        body: data,
      }),
      transformResponse: (response: ApiOk<any>) => response?.data,
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: "ServiceOffers", id: "LIST" },
        { type: "ServiceOffers", id: String(courseId) },
      ],
    }),
    getServiceSeriesBookings: builder.query<any[], string | number>({
      query: (seriesId) => ({
        url: `/service-series/${seriesId}/bookings`,
        method: "GET",
      }),
      transformResponse: (response: ApiOk<{ bookings?: unknown[] }>) =>
        mapServiceSeriesBookingsResponse(response?.data),
      providesTags: (_result, _error, seriesId) => [
        { type: "ServiceSeries", id: String(seriesId) },
      ],
    }),
  }),
});

export const {
  useGetAllServiceOffersQuery,
  useGetServiceOfferByIdQuery,
  useCreateServiceOfferMutation,
  useUpdateServiceOfferMutation,
  useGetServiceSeriesByIdQuery,
  useUpdateServiceSeriesMutation,
  useCreateServiceSeriesMutation,
  useGetServiceSeriesBookingsQuery,
} = serviceSeriesApi;
